// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory
import fetch from 'cross-fetch';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

import { apiBaseUrl } from './apiBaseUrl';

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // Determine how to serialize the body based on Content-Type
  let bodyMaybe = {};
  if (body) {
    if (!headers || headers['Content-Type'] === 'application/transit+json') {
      // Transit format (default)
      bodyMaybe = { body: serialize(body) };
    } else if (headers['Content-Type'] === 'application/json') {
      // JSON format - stringify the body
      bodyMaybe = { body: JSON.stringify(body) };
    }
  }

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      // The server's error body when it sent one; a host's error page (a 502) is not JSON.
      // Either way the error carries the response's status.
      return res
        .json()
        .catch(() => null)
        .then(data => {
          const e = Object.assign(new Error(), data && typeof data === 'object' ? data : {});
          e.status = res.status;
          throw e;
        });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// GET
const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// DELETE
const del = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.DELETE,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
//
// Every privileged transition after checkout (a label, a return, a
// cancellation...) goes the same way: `{ bodyParams: { id, transition,
// params } }`, where params is what that step needs. The server decides
// everything else and answers with the transaction, plus `result` for what
// the step produced (server/api/order-step.js).
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Removes a shop's replaced or removed photos from storage. Answers { success }.
export const deleteShopPhotos = urls => {
  return post(
    '/api/upload/shop-photo/delete',
    { urls },
    { headers: { 'Content-Type': 'application/json' } }
  );
};

// The seller puts what a refunded return brought back in stock again.
// Answers { already, listings } (server/api/order-relist.js).
export const relistOrderItems = body => {
  return post('/api/order-relist', body);
};

// What the order's open return refunds the buyer, as the refund itself works it out.
// Answers { refundCents, returnLabelCents, currency, ... } (server/api/return-refund-preview.js).
export const fetchOpenReturnRefund = transactionId => {
  return get(`/api/return-refund/${transactionId}`);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Signup proxy — routes through our server for honeypot, rate limiting, and OOPSpam checks.
// See `server/api/auth/signup.js` for the full validation flow.
export const signupUser = body => {
  return post('/api/auth/signup', body);
};

// Shipping
export const getShippingCarriers = () => {
  return get('/api/shipping/get-carriers');
};

// Per-category listing counts for the Shop by Category column (cached server-side)
export const getCategoryCounts = () => {
  return get('/api/category-counts');
};

// v1 facets (presence) for the browse filter panel — raw UI params pass through
export const getSearchSuggestions = keywords =>
  get(`/api/search-suggestions?keywords=${encodeURIComponent(keywords)}`);

export const getFacets = params => {
  const query = Object.keys(params || {})
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
  return get(`/api/facets${query ? `?${query}` : ''}`);
};

export const validateAddressOnly = body => {
  return post('/api/shipping/validate-address-only', body);
};

export const createAndVerifyAddress = body => {
  return post('/api/shipping/create-verify-address', body);
};

export const fetchCarriersAndRates = body => {
  return post('/api/shipping/fetch-carriers-and-rates', body);
};

// Record a checkout "Use this address anyway" override in the admin panel's
// audit log. Resolves { overrideId } (null when the panel is unreachable or
// not configured — the override itself must never be blocked by logging).
export const logAddressOverride = body => {
  return post('/api/shipping/address-override-log', body);
};

export const submitManualTracking = body => {
  return post('/api/shipping/manual-tracking', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const createLabelPaymentIntent = body => {
  return post('/api/stripe/create-label-payment-intent', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

/**
 * Sharetribe API module for interacting with user data.
 */
export const SharetribeApi = {
  /**
   * Fetches the shipping details of a specific user by their userId.
   * @param {string} userId - The unique identifier of the user.
   * @returns {Promise<Object>} A promise that resolves to the user's shipping details.
   */
  getUserShippingDetails: userId => {
    return get(`/api/users/shipping-details/${userId}`);
  },
};

/**
 * Fetch categories + listing fields for the P7 cutover (runtime config).
 * Returns { source, categories?, listingFields? } — when source is a
 * tradtree variant the app keeps its baked-in files.
 */
export const fetchListingConfig = () => {
  return get('/api/listing-config', { headers: { 'Content-Type': 'application/json' } });
};

/**
 * The site's content from the admin panel (the landing page's shop links).
 * @returns {Promise<Object>} { shopLinks: { data, meta } }
 */
export const fetchSiteContent = () => {
  return get('/api/content', { headers: { 'Content-Type': 'application/json' } });
};

// Check username availability
//
// This endpoint checks if a username is already taken by querying
// the Sharetribe API for users with the given username in publicData.
//
// Parameters:
// - username: The username to check for availability
//
// Returns:
// - available: boolean indicating if username is available
// - message: descriptive message about the availability
export const checkUsernameAvailability = username => {
  return post('/api/users/check-username', { username });
};

// Get user by username
//
// This endpoint finds a user by their username and returns their user ID and data.
//
// Parameters:
// - username: The username to search for
//
// Returns:
// - userId: UUID of the user
// - user: Full user object with profile data
export const getUserByUsername = username => {
  return get(`/api/users/username/${username}`, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Update transaction notification count
//
// This endpoint can increment or reset the notification count for a specific actor
// (customer or provider) in the transaction metadata.
//
// Parameters:
// - transactionId: UUID of the transaction to update
// - actor: 'customer' or 'provider' - which party to update notification for
// - action: 'increment' or 'reset' (optional, defaults to 'increment')
//
// Returns:
// - status: HTTP status code
// - message: Success message
// - data: Updated transaction data
export const updateTransactionNotification = body => {
  return post('/api/update-notification', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// ================ TRADMARKET API ================ //

/**
 * TradMarket API - handles Stripe Separate Charges & Transfers
 *
 * This communicates with the TradMarket API for processing payments,
 * managing payouts, and handling refunds through Stripe.
 */
export const BackgroundServiceApi = {
  /**
   * Create a Payment Intent for checkout
   *
   * This calls the TradMarket API to create a Stripe Payment Intent.
   * The TradMarket API handles fee calculations,
   * and creates transaction records.
   *
   * @param {Object} params - Payment Intent creation parameters
   * @param {Object} params.cartData - Cart data with seller groupings
   * @param {string} params.buyerId - ShareTribe user ID of the buyer
   * @param {Object} params.shippingAddress - Buyer's shipping address
   * @param {Object} params.billingAddress - Buyer's billing address
   * @param {Array} params.listingIds - Array of listing IDs being purchased
   * @param {string} params.currency - Currency code (e.g., 'usd')
   * @returns {Promise<Object>} Payment Intent data with clientSecret
   */
  createPaymentIntent: params => {
    return post('/api/create-payment-intent', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Get seller's available balance
   *
   * Retrieves available and pending balance for a seller.
   *
   * @param {string} sellerId - ShareTribe user ID of the seller
   * @returns {Promise<Object>} Balance data with available/pending amounts and transactions
   */
  getSellerBalance: sellerId => {
    return get(`/api/payment-service/seller-balance/${sellerId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Create or retrieve Stripe Customer
   *
   * If customerId is provided, retrieves existing customer.
   * Otherwise creates a new Stripe customer.
   *
   * @param {Object} params - Customer parameters
   * @param {string} params.userId - Sharetribe user ID
   * @param {string} params.email - Customer email
   * @param {string} params.customerId - (Optional) Existing Stripe customer ID
   * @returns {Promise<Object>} Stripe Customer object
   */
  createOrRetrieveCustomer: params => {
    return post('/api/payment-service/customer/create-or-retrieve', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Attach payment method to customer
   *
   * @param {Object} params - Parameters
   * @param {string} params.customerId - Stripe customer ID
   * @param {string} params.paymentMethodId - Stripe payment method ID
   * @returns {Promise<Object>} Payment method object
   */
  attachPaymentMethod: params => {
    return post('/api/payment-service/customer/attach-payment-method', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * List customer payment methods
   *
   * @param {string} customerId - Stripe customer ID
   * @returns {Promise<Object>} Array of payment methods
   */
  listPaymentMethods: customerId => {
    return get(`/api/payment-service/customer/${customerId}/payment-methods`, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Delete payment method
   *
   * @param {string} paymentMethodId - Stripe payment method ID
   * @returns {Promise<Object>} Success message
   */
  deletePaymentMethod: paymentMethodId => {
    return del(`/api/payment-service/customer/payment-method/${paymentMethodId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Create Setup Intent for saving payment methods
   *
   * @param {Object} params - Parameters
   * @param {string} params.customerId - (Optional) Stripe customer ID
   * @returns {Promise<Object>} Setup Intent with clientSecret
   */
  createSetupIntent: params => {
    return post('/api/payment-service/customer/create-setup-intent', params || {}, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  // ================ STRIPE CONNECT ================ //

  /**
   * Create a Stripe Express Connect account for seller
   *
   * Optimized: If returnUrl and refreshUrl are provided, also creates
   * onboarding link in the same request (saves ~2-3 seconds).
   *
   * @param {Object} params - Account creation parameters
   * @param {string} params.country - Two-letter country code (e.g., 'US', 'DE')
   * @param {string} params.businessType - 'individual' or 'company'
   * @param {string} [params.returnUrl] - URL to redirect after onboarding (optional)
   * @param {string} [params.refreshUrl] - URL to redirect if link expires (optional)
   * @returns {Promise<Object>} Created account with accountId and optional onboardingUrl
   */
  createConnectAccount: params => {
    return post('/api/payment-service/stripe-connect/create-account', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Create an onboarding link for Stripe Connect
   *
   * @param {Object} params - Account link parameters
   * @param {string} params.accountId - Stripe Connect account ID
   * @param {string} params.refreshUrl - URL to redirect if link expires
   * @param {string} params.returnUrl - URL to redirect after onboarding
   * @returns {Promise<Object>} Account link with URL
   */
  createConnectAccountLink: params => {
    return post('/api/payment-service/stripe-connect/create-account-link', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * A one-time link into the seller's own Stripe Express dashboard (bank details, payouts,
   * tax forms). The server finds the seller's account itself.
   *
   * @returns {Promise<Object>} { success, url }
   */
  createConnectLoginLink: () => {
    return post(
      '/api/payment-service/stripe-connect/login-link',
      {},
      { headers: { 'Content-Type': 'application/json' } }
    );
  },

  /**
   * Get Stripe Connect account status
   *
   * @param {string} accountId - Stripe Connect account ID
   * @returns {Promise<Object>} Account status with chargesEnabled, payoutsEnabled, etc.
   */
  getConnectAccountStatus: accountId => {
    return get(`/api/payment-service/stripe-connect/account-status/${accountId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Notify TradMarket API that seller completed Stripe onboarding
   * This triggers auto-transfer of any available funds
   *
   * @param {Object} params - Parameters
   * @param {string} params.sellerId - Seller's Sharetribe user ID
   * @param {string} params.accountId - Stripe Connect account ID
   * @returns {Promise<Object>} Auto-transfer result
   */
  notifyStripeOnboardingComplete: params => {
    return post('/api/payment-service/stripe-connect/onboarding-complete', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  // ================ RETURNS ================ //

  /**
   * What a return label for the current return would cost. It is bought
   * through the order's step (customer-buy-return-label or
   * provider-send-return-label), which prices it again itself.
   * @param {Object} params - { transactionId, parcel? } (parcel in inches/ounces)
   */
  quoteReturnLabel: params => {
    return post('/api/return-label-quote', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * What a return would refund the buyer, worked out as the refund itself is.
   * @param {Object} params - { transactionId, returnReason, items? }
   */
  returnRefundPreview: params => {
    return post('/api/return-refund-preview', params, {
      headers: { 'Content-Type': 'application/json' },
    });
  },
};

/**
 * Uploads a photo for an order (a return's, a dispute's, proof of delivery)
 * to the order's own folder; only its buyer or seller can. Resolves with the
 * photo's link, which the order's steps accept.
 */
export const uploadOrderPhoto = (file, transactionId) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('transactionId', transactionId);
  return fetch(`${apiBaseUrl()}/api/upload/order-photo`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  }).then(async res => {
    const data = await res.json().catch(() => ({}));
    // The field says it couldn't be uploaded (OrderPhotosField.uploadFailed)
    if (!res.ok || !data.url) throw Object.assign(new Error(data.message), { status: res.status });
    return data.url;
  });
};

// Shopify OAuth bridge — authenticate seller and get authorization code for TradSync
export const shopifyAuthorize = body => {
  return post('/api/shopify/authorize', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Newsletter subscription
//
// Subscribe an email to the newsletter via Brevo
//
// Parameters:
// - email: The email address to subscribe
//
// Returns:
// - success: boolean indicating if subscription was successful
// - alreadySubscribed: boolean indicating if email is already subscribed
// - message: descriptive message about the result
export const subscribeToNewsletter = email => {
  return post(
    '/api/newsletter/subscribe',
    { email },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );
};
